import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Copy, Check, Users } from "lucide-react";
import WorldMap from "@/components/WorldMap";
import { WaIcon, TgIcon, XIcon, SHARE_BTN_CLS } from "@/components/BrandIcons";
import { copyText } from "@/utils/clipboard";
import { useLang } from "@/i18n/LanguageContext";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Vidéos de bienvenue par langue — ajouter une entrée quand une nouvelle langue est fournie
const WELCOME_VIDEOS = {
  fr: "/videos/welcome-fr.mp4",
  en: "/videos/welcome-en.mp4",
  es: "/videos/welcome-es.mp4",
  vi: "/videos/welcome-vi.mp4",
  hr: "/videos/welcome-hr.mp4",
  ar: "/videos/welcome-ar.mp4",
};

// Pays arabophones : la vidéo arabe suit le pays du membre (pas de page arabe dédiée)
const AR_COUNTRIES = new Set([
  "DZ", "BH", "KM", "DJ", "EG", "IQ", "JO", "KW", "LB", "LY",
  "MR", "MA", "OM", "QA", "SA", "SO", "TN", "AE", "YE",
]);

const welcomeVideoLang = (reg) => {
  if (reg.country && AR_COUNTRIES.has(reg.country)) return "ar";
  return WELCOME_VIDEOS[reg.lang] ? reg.lang : null;
};

export const Welcome = () => {
  const { username } = useParams();
  const { lang, setLang, t } = useLang();
  const [reg, setReg] = useState(undefined);
  const [copied, setCopied] = useState(false);

  // La page de bienvenue s'affiche dans la langue d'inscription du membre
  useEffect(() => {
    if (reg && reg.lang && reg.lang !== lang) setLang(reg.lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reg]);

  useEffect(() => {
    let on = true;
    fetch(`${API}/registrations/${encodeURIComponent(username)}`)
      .then(async (r) => {
        const j = await r.json();
        if (on) setReg(r.ok ? j : null);
      })
      .catch(() => {
        if (on) setReg(null);
      });
    return () => {
      on = false;
    };
  }, [username]);

  useEffect(() => {
    if (reg && reg.username) {
      try {
        localStorage.setItem("prenet-ref", reg.username.toLowerCase());
      } catch {}
    }
  }, [reg]);

  const pageUrl = reg ? `${window.location.origin}/r/${reg.username}` : window.location.href;
  const shareText = encodeURIComponent(t.form.shareText);
  const shareLinks = {
    whatsapp: `https://wa.me/?text=${shareText}%20${encodeURIComponent(pageUrl)}`,
    x: `https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(pageUrl)}`,
    telegram: `https://t.me/share/url?url=${encodeURIComponent(pageUrl)}&text=${shareText}`,
  };
  const copyLink = async () => {
    const ok = await copyText(pageUrl);
    setCopied(ok);
    if (ok) setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-night text-ink">
      <Seo noindex />
      <WorldMap className="absolute inset-0 h-full w-full" />
      <div className="panel-line pointer-events-none absolute inset-0 opacity-50" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0A1220_80%)]" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-3xl flex-col items-center justify-center px-5 py-16 text-center">
        <Link to="/" data-testid="welcome-logo-link" className="flex items-center gap-3">
          <img src="/logo.png" alt="PreNet" className="h-12 w-auto mx-auto" data-testid="welcome-logo-image" />
        </Link>

        {reg === undefined && (
          <p className="mt-12 font-mono text-xs uppercase tracking-[0.2em] text-ink-subtle" data-testid="welcome-loading">
            …
          </p>
        )}

        {reg === null && (
          <div className="mt-12" data-testid="welcome-not-found">
            <h1 className="font-display text-xl font-bold text-ink sm:text-2xl">
              {t.welcome.notFound}
            </h1>
            <a
              href={reg ? `/?ref=${reg.username}#register` : "/#register"}
              data-testid="welcome-register-cta"
              className="mt-8 inline-block rounded-full bg-tactical px-8 py-4 text-sm font-bold text-night transition-all hover:glow-tactical"
            >
              {t.welcome.notFoundCta}
            </a>
          </div>
        )}

        {reg && (
          <div className="mt-8" data-testid="welcome-page">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-signal">
              PRENET · THE ULTIMATE NETWORK
            </p>
            <h1 className="mt-5 font-display text-3xl font-bold leading-tight text-ink sm:text-4xl lg:text-5xl">
              {t.welcome.title.replace("{name}", reg.firstName || "")}
            </h1>
            <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-ink-muted sm:text-base">
              {t.welcome.sub}
            </p>

            {welcomeVideoLang(reg) ? (
              <div className="mx-auto mt-8 w-full max-w-md" data-testid="welcome-video-block">
                <p
                  className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-violet-400"
                  data-testid="welcome-video-title"
                >
                  {t.welcome.videoTitle}
                </p>
                <video
                  controls
                  playsInline
                  preload="metadata"
                  poster={`/videos/welcome-${welcomeVideoLang(reg)}.jpg`}
                  className="mt-3 w-full rounded-2xl drop-shadow-[0_0_35px_rgba(139,92,246,0.35)]"
                  data-testid="welcome-video"
                >
                  <source src={`/videos/welcome-${welcomeVideoLang(reg)}.mp4`} type="video/mp4" />
                </video>
              </div>
            ) : null}

            <Link
              to="/membre"
              data-testid="welcome-members-cta"
              className="mx-auto mt-6 flex w-full max-w-md items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-tactical via-signal to-tactical bg-[length:200%_100%] bg-left px-8 py-4 text-sm font-bold uppercase tracking-[0.08em] text-night transition-[background-position,box-shadow,transform] duration-300 hover:scale-[1.01] hover:bg-right hover:glow-signal"
            >
              <Users size={16} />
              {t.member.dashLink}
            </Link>

            <div className="glass mx-auto mt-10 max-w-xs rounded-3xl p-7" data-testid="welcome-position-card">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-subtle">
                {t.welcome.positionLabel}
              </p>
              <p className="mt-2 font-display text-6xl font-bold text-signal text-glow-signal" data-testid="welcome-position-number">
                #{reg.position}
              </p>
              <p className="mt-3 font-mono text-xs tracking-[0.15em] text-ink-muted" data-testid="welcome-username">
                @{reg.username}
              </p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-ink-subtle">
                {t.welcome.memberLabel}
              </p>
              {reg.country ? (
                <p className="mt-3 flex items-center gap-2 text-xs text-ink-muted" data-testid="welcome-country">
                  <img
                    src={`/flags/${reg.country.toLowerCase()}.png`}
                    width="18"
                    height="12"
                    alt=""
                    className="shrink-0 rounded-[2px] object-cover"
                    style={{ boxShadow: "0 0 0 1px rgba(255,255,255,0.14)" }}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  <span>
                    {(() => {
                      try {
                        return new Intl.DisplayNames([lang], { type: "region" }).of(reg.country) || reg.country;
                      } catch {
                        return reg.country;
                      }
                    })()}
                  </span>
                </p>
              ) : null}
              {reg.sponsor ? (
                <p className="mt-4 border-t border-white/8 pt-3.5 text-xs text-ink-muted" data-testid="welcome-sponsor">
                  {t.welcome.placedUnder}{" "}
                  <span className="font-mono font-bold text-signal">@{reg.sponsor}</span>
                </p>
              ) : null}
            </div>

            <div className="glass mx-auto mt-5 max-w-xs rounded-2xl p-4" data-testid="welcome-referral-box">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-subtle">
                {t.form.referralLinkLabel}
              </p>
              <p className="mt-2 truncate font-mono text-xs text-signal2" data-testid="welcome-referral-link">
                {reg ? `${window.location.origin}/r/${reg.username}` : ""}
              </p>
            </div>

            <div className="mt-8 flex items-center justify-center gap-3">
              <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" data-testid="welcome-share-whatsapp" className={SHARE_BTN_CLS}>
                <WaIcon />
              </a>
              <a href={shareLinks.x} target="_blank" rel="noopener noreferrer" aria-label="X" data-testid="welcome-share-x" className={SHARE_BTN_CLS}>
                <XIcon />
              </a>
              <a href={shareLinks.telegram} target="_blank" rel="noopener noreferrer" aria-label="Telegram" data-testid="welcome-share-telegram" className={SHARE_BTN_CLS}>
                <TgIcon />
              </a>
              <button type="button" onClick={copyLink} aria-label="Copy link" data-testid="welcome-copy-button" className={SHARE_BTN_CLS}>
                {copied ? <Check size={15} className="text-signal2" /> : <Copy size={15} />}
              </button>
            </div>
            {copied && (
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-signal2" data-testid="welcome-copied-label">
                {t.form.shareCopied}
              </p>
            )}

            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Link to="/" data-testid="welcome-home-link" className="text-sm font-semibold text-ink-muted transition-colors hover:text-signal">
                {t.welcome.home}
              </Link>
              <a href={reg ? `/?ref=${reg.username}#register` : "/#register"} data-testid="welcome-register-link" className="text-sm font-semibold text-signal underline-offset-4 hover:underline">
                {t.welcome.notFoundCta}
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Welcome;